import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge, Table } from 'react-bootstrap';
import { FaRocket, FaCheckCircle, FaExclamationTriangle, FaFileAlt, FaDownload, FaSearch, FaLightbulb } from 'react-icons/fa';
import Layout from './Layout';
import axios from 'axios';
import '../assets/css/SpSeoCampaigns.css';

const SpSeoCampaigns = () => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

    const cloneCsvFile = async (file) => {
        if (!file) return null;
        const buffer = await file.arrayBuffer();
        return new File([buffer], file.name, { type: file.type || 'text/csv', lastModified: Date.now() });
    };

    const getRequestErrorMessage = (err, fallback) => {
        if (err?.code === 'ERR_UPLOAD_FILE_CHANGED') {
            return 'The selected file changed while uploading. Please re-select the CSV file and try again.';
        }
        if (err?.code === 'ERR_NETWORK') {
            return 'Network error. Verify backend is running and reachable at ' + API_URL + '.';
        }
        return err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;
    };

    const buildCreationWarning = (errors) => {
        if (!Array.isArray(errors) || errors.length === 0) {
            return null;
        }

        const details = errors.slice(0, 3).map((item) => {
            const campaignName = item?.campaignName || 'Unknown campaign';
            const reason = Array.isArray(item?.errors) && item.errors.length > 0
                ? item.errors[0]
                : 'Unknown error';
            return `${campaignName}: ${reason}`;
        });

        const notAuthorized = errors.some((item) =>
            Array.isArray(item?.errors) && item.errors.some((msg) =>
                typeof msg === 'string' && msg.includes('User is not allowed to access the requested products')
            )
        );

        const intro = notAuthorized
            ? 'Some campaigns were not created because the ASIN/SKU are not authorized for the selected Amazon profile. Use products that already belong to this profile and try again.'
            : 'Some campaigns could not be created in Amazon. Review the first failed campaigns below.';

        return {
            intro,
            details
        };
    };

    // Estados para configuracion
    const [siteType, setSiteType] = useState('AMAZON_AND_BEYOND');

    // Estados para archivos
    const [productsFile, setProductsFile] = useState(null);
    const [keywordsFile, setKeywordsFile] = useState(null);

    // Estados para keywords manuales
    const [manualKeywords, setManualKeywords] = useState('');

    // Estados de configuracion de campaign
    const [defaultBid, setDefaultBid] = useState(0.75);
    const [defaultBudget, setDefaultBudget] = useState(10);
    const [campaignState, setCampaignState] = useState('PAUSED');

    // Estados de UI
    const [loading, setLoading] = useState(false);
    const [suggestingKeywords, setSuggestingKeywords] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [creationComplete, setCreationComplete] = useState(false);

    // Bulk actions states
    const [bulkAction, setBulkAction] = useState('');
    const [bulkBudget, setBulkBudget] = useState('');
    const [bulkBid, setBulkBid] = useState('');

    // Manejar carga de archivo de productos
    const handleProductsFileChange = async (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            try {
                const stableFile = await cloneCsvFile(file);
                setProductsFile(stableFile);
                setError(null);
            } catch (err) {
                setError('Could not read products CSV file. Please try selecting it again.');
            }
        } else {
            setError('Please select a valid CSV file');
        }
    };

    // Manejar carga de archivo de keywords
    const handleKeywordsFileChange = async (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            try {
                const stableFile = await cloneCsvFile(file);
                setKeywordsFile(stableFile);
                setError(null);
            } catch (err) {
                setError('Could not read keywords CSV file. Please try selecting it again.');
            }
        } else {
            setError('Please select a valid CSV file');
        }
    };

    // Sugerir keywords basado en ASINs
    const handleSuggestKeywords = async () => {
        if (!productsFile) {
            setError('First, upload the product file to indicate keywords.');
            return;
        }

        setSuggestingKeywords(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('products_file', productsFile);
            formData.append('match_type', 'EXACT'); // Solo EXACT para SEO

            const response = await axios.post(`${API_URL}/sp-seo-campaigns/suggest-keywords`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                // Agregar keywords sugeridas al campo manual
                const existingKeywords = manualKeywords.trim();
                const newKeywords = response.data.keywords.join(', ');
                setManualKeywords(existingKeywords ? `${existingKeywords}, ${newKeywords}` : newKeywords);
                setResult({
                    success: true,
                    message: `${response.data.keywords.length} keywords sugeridas (EXACT match)`
                });
            } else {
                setError(response.data.error || 'Error suggesting keywords');
            }
        } catch (err) {
            console.error('Error:', err);
            setError(getRequestErrorMessage(err, 'Error suggesting keywords'));
        } finally {
            setSuggestingKeywords(false);
        }
    };

    // Generate SEO campaigns
    const handleGenerateCampaigns = async () => {
        if (!productsFile) {
            setError('You must upload the products file (ASIN, SKU)');
            return;
        }

        if (!manualKeywords.trim() && !keywordsFile) {
            setError('You must enter keywords manually or upload a keywords file');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const formData = new FormData();
            formData.append('products_file', productsFile);
            formData.append('site_type', siteType);
            formData.append('default_bid', defaultBid);
            formData.append('default_budget', defaultBudget);

            if (keywordsFile) {
                formData.append('keywords_file', keywordsFile);
            }

            if (manualKeywords.trim()) {
                formData.append('manual_keywords', manualKeywords.trim());
            }

            const response = await axios.post(`${API_URL}/sp-seo-campaigns/generate`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                const generatedCampaigns = response.data.campaigns || [];

                // Agregar campo 'selected' a cada campaign
                const campaignsWithSelection = generatedCampaigns.map(campaign => ({
                    ...campaign,
                    selected: true
                }));

                setCampaigns(campaignsWithSelection);

                setResult({
                    success: true,
                    message: `${generatedCampaigns.length} SEO campaigns generated. Select which to create in Amazon.`,
                    count: generatedCampaigns.length
                });
            } else {
                setError(response.data.error || 'Error generating campaigns');
            }
        } catch (err) {
            console.error('Error:', err);
            setError(getRequestErrorMessage(err, 'Error generating campaigns'));
        } finally {
            setLoading(false);
        }
    };

    // Create campaigns en Amazon
    const handleCreateInAmazon = async () => {
        const selectedCampaigns = campaigns.filter(c => c.selected);

        if (selectedCampaigns.length === 0) {
            setError('You must select at least one campaign to create in Amazon');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const profileId = localStorage.getItem('selectedAccountValue');

            if (!profileId) {
                setError('Please select an account in the selector above before creating campaigns');
                setLoading(false);
                return;
            }

            const response = await axios.post(`${API_URL}/sp-seo-campaigns/create-in-amazon`, {
                profileId,
                campaigns: selectedCampaigns,
                campaignState,
                siteType
            });

            if (response.data.success) {
                const amazonResults = response.data.results || [];
                const errors = response.data.errors || [];
                const warning = buildCreationWarning(errors);

                setResult({
                    success: true,
                    message: response.data.message,
                    count: response.data.campaigns_created,
                    amazonResults: amazonResults,
                    errors: errors,
                    warning
                });

                const campaignsForTable = amazonResults.map(result => ({
                    'Campaign Name': result.campaignName,
                    'Campaign ID': result.campaignId,
                    'Ad Group ID': result.adGroupId,
                    'Keyword': result.keyword,
                    'Match Type': 'EXACT',
                    'Status': 'Created in Amazon'
                }));

                setCampaigns(campaignsForTable);
                setCreationComplete(true);
            } else {
                setError(response.data.error || 'Error creating campaigns en Amazon');
            }
        } catch (err) {
            console.error('Error:', err);
            setError(getRequestErrorMessage(err, 'Error creating campaigns in Amazon'));
        } finally {
            setLoading(false);
        }
    };

    // Toggle seleccion
    const toggleCampaignSelection = (index) => {
        const updatedCampaigns = [...campaigns];
        updatedCampaigns[index].selected = !updatedCampaigns[index].selected;
        setCampaigns(updatedCampaigns);
    };

    const selectAllCampaigns = () => {
        const updatedCampaigns = campaigns.map(c => ({ ...c, selected: true }));
        setCampaigns(updatedCampaigns);
    };

    const deselectAllCampaigns = () => {
        const updatedCampaigns = campaigns.map(c => ({ ...c, selected: false }));
        setCampaigns(updatedCampaigns);
    };

    // Apply accion masiva
    const applyBulkAction = () => {
        if (!bulkAction) {
            setError('Select a bulk action');
            return;
        }

        const selectedCount = campaigns.filter(c => c.selected).length;
        if (selectedCount === 0) {
            setError('Select al menos una campaign');
            return;
        }

        const updatedCampaigns = campaigns.map(campaign => {
            if (!campaign.selected) return campaign;

            const updated = { ...campaign };

            switch (bulkAction) {
                case 'budget':
                    if (bulkBudget && parseFloat(bulkBudget) > 0) {
                        updated.Budget = parseFloat(bulkBudget);
                    }
                    break;
                case 'bid':
                    if (bulkBid && parseFloat(bulkBid) > 0) {
                        updated.Bid = parseFloat(bulkBid);
                    }
                    break;
                default:
                    break;
            }

            return updated;
        });

        setCampaigns(updatedCampaigns);
        setBulkAction('');
        setBulkBudget('');
        setBulkBid('');
        setError(null);
        setResult({
            success: true,
            message: `Accion aplicada a ${selectedCount} campaign(s)`,
            count: selectedCount
        });
    };

    // Descargar CSV
    const handleDownloadCampaigns = () => {
        if (campaigns.length === 0) return;

        const headers = Object.keys(campaigns[0]).filter(h => h !== 'selected');
        const csvContent = [
            headers.join(','),
            ...campaigns.map(campaign =>
                headers.map(header => {
                    const value = campaign[header];
                    if (typeof value === 'string' && value.includes(',')) {
                        return `"${value}"`;
                    }
                    return value;
                }).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `sp_seo_campaigns_${Date.now()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Reset
    const handleReset = () => {
        setProductsFile(null);
        setKeywordsFile(null);
        setManualKeywords('');
        setCampaigns([]);
        setResult(null);
        setError(null);
        setCreationComplete(false);
    };

    return (
        <Layout>
            <Container fluid className="sp-seo-campaigns-page py-4">
                <Row className="mb-4">
                    <Col>
                        <h2 className=" mb-3">
                            <FaSearch className="me-2" />
                            SEO Campaigns - Sponsored Products
                        </h2>
                        <p className="text-muted">
                            Create SP campaigns optimized for SEO with Keyword Targeting (EXACT match) and Dynamic Bids - Down Only
                        </p>
                    </Col>
                </Row>

                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        <FaExclamationTriangle className="me-2" />
                        {error}
                    </Alert>
                )}

                {result && result.success && !creationComplete && (
                    <Alert variant="success" onClose={() => setResult(null)} dismissible>
                        <FaCheckCircle className="me-2" />
                        {result.message}
                    </Alert>
                )}

                {result?.warning && (
                    <Alert variant="warning" onClose={() => setResult({ ...result, warning: null })} dismissible>
                        <FaExclamationTriangle className="me-2" />
                        <div>{result.warning.intro}</div>
                        {result.warning.details.map((line, idx) => (
                            <div key={idx} className="small mt-1">- {line}</div>
                        ))}
                    </Alert>
                )}

                {/* Configuration Section */}
                {!result?.amazonResults && (
                    <>
                        {/* Info Banner */}
                        <Alert variant="info" className="mb-4">
                            <FaLightbulb className="me-2" />
                            <strong>Configuration SEO:</strong> Manual Targeting • Keyword Targeting • EXACT Match • Dynamic Bids (Down Only)
                        </Alert>

                        {/* SITE Selection */}
                        <div className="config-section-card mb-4">
                            <h5 className="mb-3">
                                <Badge bg="primary" className="me-2">1</Badge>
                                Select SITE
                            </h5>
                            <Form.Select
                                value={siteType}
                                onChange={(e) => setSiteType(e.target.value)}
                                className="site-selector"
                            >
                                <option value="AMAZON_AND_BEYOND">Amazon and Beyond (Multi-Marketplace)</option>
                                <option value="AMAZON_BUSINESS">Amazon Business (Single Marketplace)</option>
                            </Form.Select>
                        </div>

                        {/* Upload Grid */}
                        <div className="upload-grid">
                            {/* Card 1: Products CSV */}
                            <div className={`upload-card-new ${productsFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Required</div>
                                <Badge bg="primary" className="step-badge">2</Badge>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Products CSV</div>
                                <div className="upload-card-desc">
                                    Required columns: ASIN, SKU
                                </div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">
                                        {productsFile ? 'Change File' : 'Select File'}
                                    </div>
                                    <input type="file" accept=".csv" onChange={handleProductsFileChange} hidden />
                                </label>
                                {productsFile && <small className="text-success mt-2">{productsFile.name}</small>}
                            </div>

                            {/* Card 2: Keywords CSV (Optional) */}
                            <div className={`upload-card-new ${keywordsFile ? 'has-file' : ''}`}>
                                <Badge bg="secondary" className="step-badge">3a</Badge>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Keywords CSV (Optional)</div>
                                <div className="upload-card-desc">
                                    Column: Keyword (one per line)
                                </div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">
                                        {keywordsFile ? 'Change File' : 'Select File'}
                                    </div>
                                    <input type="file" accept=".csv" onChange={handleKeywordsFileChange} hidden />
                                </label>
                                {keywordsFile && <small className="text-success mt-2">{keywordsFile.name}</small>}
                            </div>
                        </div>

                        {/* Manual Keywords Section */}
                        <div className="config-section-card mt-4">
                            <h5 className="mb-3">
                                <Badge bg="secondary" className="me-2">3b</Badge>
                                Manual Keywords
                            </h5>
                            <Form.Group>
                                <Form.Label className="text-muted">
                                    Enter keywords separated by comma (Match Type: EXACT fixed)
                                </Form.Label>
                                <div className="d-flex gap-2">
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="keyword1, keyword2, keyword3..."
                                        value={manualKeywords}
                                        onChange={(e) => setManualKeywords(e.target.value)}
                                        className="keywords-textarea"
                                    />
                                </div>
                                <div className="mt-2">
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        onClick={handleSuggestKeywords}
                                        disabled={suggestingKeywords || !productsFile}
                                    >
                                        {suggestingKeywords ? (
                                            <>
                                                <Spinner size="sm" animation="border" className="me-1" />
                                                Suggesting...
                                            </>
                                        ) : (
                                            <>
                                                <FaLightbulb className="me-1" />
                                                Suggest Keywords (EXACT)
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form.Group>
                        </div>

                        {/* Campaign Configuration */}
                        <div className="config-section-card mt-4">
                            <h5 className="mb-3">
                                <Badge bg="primary" className="me-2">4</Badge>
                                Configuration of Campaign
                            </h5>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="text-muted">Bid Default ($)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0.02"
                                            value={defaultBid}
                                            onChange={(e) => setDefaultBid(parseFloat(e.target.value))}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="text-muted">Daily Budget ($)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="1"
                                            min="1"
                                            value={defaultBudget}
                                            onChange={(e) => setDefaultBudget(parseFloat(e.target.value))}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="text-muted">Targeting Type</Form.Label>
                                        <Form.Control type="text" value="Manual Targeting" disabled className=" " />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="text-muted">Bidding Strategy</Form.Label>
                                        <Form.Control type="text" value="Dynamic Bids - Down Only" disabled className=" " />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="text-muted">Match Type</Form.Label>
                                        <Form.Control type="text" value="EXACT (Fijo)" disabled className=" text-success fw-bold" />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="text-muted">Targeting Strategy</Form.Label>
                                        <Form.Control type="text" value="Keyword Targeting" disabled className=" " />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>

                        {/* Generate Button */}
                        <div className="generate-section mt-4">
                            <button className={`btn-generate-campaigns ${(!loading && productsFile && (manualKeywords.trim() || keywordsFile)) ? 'ready' : ''}`}
                                onClick={handleGenerateCampaigns}
                                disabled={loading || !productsFile || (!manualKeywords.trim() && !keywordsFile)}
                            >
                                {loading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Generating SEO campaigns...
                                    </>
                                ) : (
                                    <>
                                        <FaRocket />
                                        Generate SEO Campaigns
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}

                {/* Results Section */}
                {result && campaigns.length > 0 && (
                    <Row>
                        <Col>
                            <Card className="config-section-card border-0 p-0 overflow-hidden">
                                <Card.Header className="  p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5><FaRocket className="me-2" />Campaigns SEO Generadas ({campaigns.length})</h5>
                                    </div>

                                    <div>
                                        {/* Bulk Actions Row */}
                                        <div className="mb-3 d-flex justify-content-between align-items-center">
                                            <div className="d-flex gap-2 align-items-center">
                                                <span className=" fw-bold">Bulk actions:</span>
                                                <Form.Select
                                                    size="sm"
                                                    value={bulkAction}
                                                    onChange={(e) => setBulkAction(e.target.value)}
                                                    style={{ width: '200px' }}
                                                >
                                                    <option value="">Select action</option>
                                                    <option value="budget">Assign budget...</option>
                                                    <option value="bid">Assign bid...</option>
                                                </Form.Select>

                                                {bulkAction === 'budget' && (
                                                    <Form.Control
                                                        type="number"
                                                        size="sm"
                                                        placeholder="New budget"
                                                        value={bulkBudget}
                                                        onChange={(e) => setBulkBudget(e.target.value)}
                                                        style={{ width: '150px' }}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                )}

                                                {bulkAction === 'bid' && (
                                                    <Form.Control
                                                        type="number"
                                                        size="sm"
                                                        placeholder="New bid"
                                                        value={bulkBid}
                                                        onChange={(e) => setBulkBid(e.target.value)}
                                                        style={{ width: '150px' }}
                                                        min="0.02"
                                                        step="0.01"
                                                    />
                                                )}

                                                {bulkAction && (
                                                    <Button
                                                        variant="warning"
                                                        size="sm"
                                                        onClick={applyBulkAction}
                                                    >
                                                        Apply
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Action Buttons Row */}
                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                            <div className="d-flex gap-2 align-items-center">
                                                <Form.Select
                                                    size="sm"
                                                    value={campaignState}
                                                    onChange={(e) => setCampaignState(e.target.value)}
                                                    style={{ width: 'auto' }}
                                                    disabled={creationComplete}
                                                >
                                                    <option value="ENABLED">Create as: Enabled</option>
                                                    <option value="PAUSED">Create as: Paused</option>
                                                </Form.Select>

                                                <Button variant="outline-success" size="sm" onClick={handleDownloadCampaigns}>
                                                    <FaDownload className="me-1" /> Download CSV
                                                </Button>
                                                <Button variant="outline-primary" size="sm" onClick={handleReset}>
                                                    <FaRocket className="me-1" /> New Generation
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0 table-responsive " style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    <Table hover  className="align-middle mb-0" size="sm" style={{ fontSize: '0.85rem' }}>
                                        <thead className="text-warning sticky-top ">
                                            <tr>
                                                <th width="40">
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={campaigns.every(c => c.selected)}
                                                        onChange={(e) => e.target.checked ? selectAllCampaigns() : deselectAllCampaigns()}
                                                    />
                                                </th>
                                                <th width="50">#</th>
                                                <th width="400">Campaign Name</th>
                                                <th width="100">ASIN</th>
                                                <th width="150">Keyword</th>
                                                <th width="80">Match Type</th>
                                                <th width="80">Bid</th>
                                                <th width="80">Budget</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {campaigns.map((campaign, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={campaign.selected || false}
                                                            onChange={() => toggleCampaignSelection(idx)}
                                                        />
                                                    </td>
                                                    <td>{idx + 1}</td>
                                                    <td>
                                                        <div style={{ wordBreak: "break-all", whiteSpace: "normal" }}>
                                                            {campaign['Campaign Name']}
                                                        </div>
                                                    </td>
                                                    <td className="text-info">{campaign.ASIN}</td>
                                                    <td className="text-warning">{campaign.Keyword}</td>
                                                    <td>
                                                        <Badge bg="success">EXACT</Badge>
                                                    </td>
                                                    <td>${campaign.Bid}</td>
                                                    <td>${campaign.Budget}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                                <Card.Footer className=" border-top border-secondary d-flex justify-content-between p-3">
                                    <div className="d-flex gap-2">
                                        <Button variant="secondary" onClick={handleReset} className="fw-bold">← Cancel</Button>
                                    </div>
                                    <div className="d-flex gap-3 align-items-center">
                                        <div className="text-muted small">
                                            Total: {campaigns.length} | Selected: {campaigns.filter(c => c.selected).length}
                                        </div>
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={handleCreateInAmazon}
                                            disabled={loading || creationComplete || campaigns.filter(c => c.selected).length === 0}
                                            style={{
                                                fontWeight: 'bold',
                                                padding: '6px 20px'
                                            }}
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-1" />
                                                    Creating in Amazon...
                                                </>
                                            ) : creationComplete ? (
                                                <>
                                                    <FaCheckCircle className="me-1" /> Campaigns Created
                                                </>
                                            ) : (
                                                <>
                                                    Create Campaigns SEO en Amazon
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Card.Footer>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Requirements Section */}
                {!result && (
                    <div className="requirements-section mt-4">
                        <span className="requirements-title">📋 SEO campaign specifications</span>
                        <div className="req-item"><strong>Products CSV:</strong> Must contain 'ASIN', 'SKU'.</div>
                        <div className="req-item"><strong>Keywords:</strong> Manual or CSV. Match Type always EXACT.</div>
                        <div className="req-item"><strong>Targeting:</strong> Manual Targeting con Keyword Targeting.</div>
                        <div className="req-item"><strong>Bidding Strategy:</strong> Dynamic Bids - Down Only.</div>
                        <div className="req-item"><strong>SITE:</strong> Amazon and Beyond o Amazon Business.</div>
                    </div>
                )}
            </Container>
        </Layout>
    );
};

export default SpSeoCampaigns;


